import React, { useState, useRef, DragEvent } from 'react'
import { Icon, IconType } from '..'
import { FileInput } from '../FileInput'

interface IProps {
  url?: string
  fileName?: string
  onChange: (file: File) => void
}

export const FileUpload: React.FC<IProps> = ({ url, fileName, onChange }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const onFileChange = (file: File) => {
    setSelectedFile(file)
    onChange(file)
  }

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type === 'application/pdf') {
        onFileChange(file)
      }
    }
  }

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div
      className={`flex flex-col justify-center rounded-md border-2 ${isDragging ? 'border-indigo-300 bg-indigo-50' : 'border-dashed border-gray-300'} px-6 pt-5 pb-6 cursor-pointer transition duration-150`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <div className='space-y-1 text-center mx-auto'>
        {!selectedFile && !url && (
          <>
            <Icon
              type={IconType.attachment}
              className='mb-3 mx-auto h-6 w-6 text-gray-400'
            />
            <div className='flex flex-col space-y-1'>
              <p className='text-sm text-gray-600'>
                <span className='font-medium text-indigo-600 hover:text-indigo-500'>
                  Click to upload
                </span>{' '}
                or drag and drop
              </p>
              <p className='text-xs text-gray-500'>PDF up to 50MB</p>
            </div>
          </>
        )}
        {(selectedFile || url) && (
          <div className='flex flex-col items-center justify-center space-y-2'>
            <div className='flex items-center'>
              <Icon
                type={IconType.attachment}
                className='h-6 w-6 text-gray-400 mr-2'
              />
              <span className='text-sm text-gray-600'>
                {selectedFile ? selectedFile.name : fileName}
              </span>
            </div>
            <button
              className='text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (fileInputRef.current) {
                  fileInputRef.current.click()
                }
              }}
            >
              <span className='font-medium text-indigo-600 hover:text-indigo-500'>
                Change PDF
              </span>{' '}
              or drag and drop
            </button>
            <p className='text-xs text-gray-500'>PDF up to 50MB</p>
          </div>
        )}
        <FileInput
          id='file-upload'
          name='file-upload'
          className='sr-only'
          onChange={onFileChange}
          ref={fileInputRef}
        />
      </div>
    </div>
  )
}
